import { useCallback, useEffect, useState, Fragment, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button } from '../../../../shared/components/index.js';

// M05 §14 (T069/T070) — Master Import Console. Drives the existing backend
// `@Controller('v1/master/import')`:
//   GET   /master/import/batches?entityType            → T069[]
//   POST  /master/import/batches {entityType,fileName,fileFormat,storagePath,totalRows,fileSizeMb} → 201
//   GET   /master/import/batches/:batchId/errors       → T070[]
//   PATCH /master/import/batches/:batchId/execute
//   PATCH /master/import/batches/:batchId/rollback
// File parsing/storage is the caller's responsibility (backend records metadata
// only), so this console registers the already-stored batch, then lets the
// operator inspect row errors, execute the import, and roll it back.

interface Batch {
  readonly batchId: string;
  readonly entityType: string;
  readonly fileName: string;
  readonly fileFormat: string;
  readonly totalRows: number;
  readonly validRows: number;
  readonly errorRows: number;
  readonly importedRows: number;
  readonly batchStatus: string;
  readonly errorSummary: string | null;
}
interface ImportError {
  readonly errorId: string;
  readonly rowNumber: number;
  readonly columnName: string | null;
  readonly errorCode: string;
  readonly errorMessage: string;
  readonly rawValue: string | null;
}

const ENTITY_TYPES = [
  'CUSTOMER', 'VENDOR', 'PAINTER', 'WORK_TYPE', 'REJECTION_REASON', 'TAT',
  'RATE_CATEGORY', 'LOOKUP_CATEGORY', 'LOOKUP_VALUE', 'NOTIFICATION_TEMPLATE',
] as const;
const FILE_FORMATS = ['XLSX', 'CSV', 'JSON', 'XML'] as const;
const EXECUTABLE = new Set(['UPLOADED', 'VALIDATED']);

const EMPTY = { entityType: 'CUSTOMER', fileName: '', fileFormat: 'XLSX', storagePath: '', totalRows: '', fileSizeMb: '' };
type Form = typeof EMPTY;

export function MasterImportConsolePage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Batch[] | null>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [errorsFor, setErrorsFor] = useState<string | null>(null);
  const [batchErrors, setBatchErrors] = useState<ImportError[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams();
      if (entityFilter) params.set('entityType', entityFilter);
      const qs = params.toString();
      const r = await apiRequest<Batch[]>(`/master/import/batches${qs ? `?${qs}` : ''}`, { token });
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, entityFilter]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const beginCreate = () => { setForm(EMPTY); setFormError(null); setShowForm(true); };

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const total = Number(form.totalRows);
    const size = Number(form.fileSizeMb);
    if (!form.fileName.trim() || !form.storagePath.trim()) { setFormError(t('mdm.import.nameStorageReq')); return; }
    if (Number.isNaN(total) || total < 0) { setFormError(t('mdm.import.totalRowsInvalid')); return; }
    if (Number.isNaN(size) || size < 0) { setFormError(t('mdm.import.fileSizeInvalid')); return; }
    setSaving(true); setFormError(null);
    try {
      await apiRequest('/master/import/batches', {
        method: 'POST', token,
        body: {
          entityType: form.entityType, fileName: form.fileName.trim(), fileFormat: form.fileFormat,
          storagePath: form.storagePath.trim(), totalRows: total, fileSizeMb: size,
        },
      });
      setShowForm(false); setForm(EMPTY);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [form, token, load]);

  const viewErrors = useCallback(async (b: Batch) => {
    if (errorsFor === b.batchId) { setErrorsFor(null); setBatchErrors(null); return; }
    setErrorsFor(b.batchId); setBatchErrors(null); setError(null);
    try {
      const r = await apiRequest<ImportError[]>(`/master/import/batches/${encodeURIComponent(b.batchId)}/errors`, { token });
      setBatchErrors(Array.isArray(r) ? r : []);
    } catch (e) { setError((e as Error).message); setBatchErrors([]); }
  }, [token, errorsFor]);

  const runAction = useCallback(async (b: Batch, action: 'execute' | 'rollback') => {
    setBusyId(b.batchId); setError(null);
    try {
      await apiRequest(`/master/import/batches/${encodeURIComponent(b.batchId)}/${action}`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusyId(null); }
  }, [token, load]);

  return (
    <MdmShell
      label="master-import-console"
      title={t('mdm.import.title')}
      description={t('mdm.import.description')}
      actions={<Button type="button" variant="primary" data-testid="new-batch" onClick={beginCreate}>{t('mdm.import.registerBatch')}</Button>}
    >
      <form aria-label="import-filters" onSubmit={(e) => { e.preventDefault(); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="imp-entity" label={t('mdm.import.entityTypeFilter')}>
          <select id="imp-entity" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {ENTITY_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label="create-batch-form" style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('mdm.import.formTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field id="b-entityType" label={t('mdm.import.entityTypeReq')}>
              <select id="b-entityType" value={form.entityType} onChange={set('entityType')} style={s.input}>
                {ENTITY_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field id="b-fileName" label={t('mdm.import.fileNameReq')}>
              <input id="b-fileName" value={form.fileName} onChange={set('fileName')} required style={s.input} />
            </Field>
            <Field id="b-fileFormat" label={t('mdm.import.fileFormatReq')}>
              <select id="b-fileFormat" value={form.fileFormat} onChange={set('fileFormat')} style={s.input}>
                {FILE_FORMATS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field id="b-storagePath" label={t('mdm.import.storagePathReq')} hint={t('mdm.import.storageHint')}>
              <input id="b-storagePath" value={form.storagePath} onChange={set('storagePath')} required style={s.input} />
            </Field>
            <Field id="b-totalRows" label={t('mdm.import.totalRowsReq')}>
              <input id="b-totalRows" type="number" value={form.totalRows} onChange={set('totalRows')} required style={s.input} />
            </Field>
            <Field id="b-fileSizeMb" label={t('mdm.import.fileSizeReq')}>
              <input id="b-fileSizeMb" type="number" step="0.01" value={form.fileSizeMb} onChange={set('fileSizeMb')} required style={s.input} />
            </Field>
          </div>
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-batch" disabled={saving}>
              {saving ? t('mdm.import.registering') : t('mdm.import.registerBatchBtn')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="batches-empty" style={{ color: s.muted }}>{t('mdm.import.empty')}</p>
      ) : (
        <table aria-label="batches-table" style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>{t('mdm.import.colEntity')}</th>
              <th style={s.th}>{t('mdm.import.colFile')}</th>
              <th style={s.th}>{t('mdm.import.colFormat')}</th>
              <th style={s.th}>{t('mdm.import.colRows')}</th>
              <th style={s.th}>{t('mdm.col.status')}</th>
              <th style={s.th}>{t('mdm.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <Fragment key={b.batchId}>
                <tr data-testid={`batch-row-${b.batchId}`}>
                  <td style={s.td}>{b.entityType}</td>
                  <td style={s.td}>{b.fileName}</td>
                  <td style={s.td}>{b.fileFormat}</td>
                  <td style={s.td}>{b.totalRows} / {b.validRows} / {b.errorRows} / {b.importedRows}</td>
                  <td style={s.td}><StatusBadge status={b.batchStatus} /></td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Button type="button" variant="secondary" data-testid={`errors-${b.batchId}`} onClick={() => viewErrors(b)}>
                        {errorsFor === b.batchId ? t('mdm.import.hideErrors') : t('mdm.import.errorsCount', { count: b.errorRows })}
                      </Button>
                      {EXECUTABLE.has(b.batchStatus) && (
                        <Button type="button" variant="primary" data-testid={`execute-${b.batchId}`} disabled={busyId === b.batchId} onClick={() => runAction(b, 'execute')}>{t('mdm.import.execute')}</Button>
                      )}
                      {b.batchStatus === 'COMPLETED' && (
                        <Button type="button" variant="danger" data-testid={`rollback-${b.batchId}`} disabled={busyId === b.batchId} onClick={() => runAction(b, 'rollback')}>{t('mdm.import.rollback')}</Button>
                      )}
                    </div>
                  </td>
                </tr>
                {errorsFor === b.batchId && (
                  <tr key={`${b.batchId}-errors`} data-testid={`batch-errors-${b.batchId}`}>
                    <td style={s.td} colSpan={6}>
                      {!batchErrors ? (
                        <p style={{ margin: 0 }}>{t('mdm.import.loadingErrors')}</p>
                      ) : batchErrors.length === 0 ? (
                        <p data-testid={`batch-errors-empty-${b.batchId}`} style={{ margin: 0, color: s.muted }}>{t('mdm.import.noRowErrors')}</p>
                      ) : (
                        <table aria-label={`batch-errors-table-${b.batchId}`} style={s.table}>
                          <thead>
                            <tr>
                              <th style={s.th}>{t('mdm.import.errColRow')}</th>
                              <th style={s.th}>{t('mdm.import.errColColumn')}</th>
                              <th style={s.th}>{t('mdm.import.errColCode')}</th>
                              <th style={s.th}>{t('mdm.import.errColMessage')}</th>
                              <th style={s.th}>{t('mdm.import.errColRaw')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batchErrors.map((er) => (
                              <tr key={er.errorId}>
                                <td style={s.td}>{er.rowNumber}</td>
                                <td style={s.td}>{er.columnName ?? '—'}</td>
                                <td style={s.td}>{er.errorCode}</td>
                                <td style={s.td}>{er.errorMessage}</td>
                                <td style={s.td}>{er.rawValue ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </MdmShell>
  );
}
